import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatIDR } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, Warning, Clock, DownloadSimple, ChartBar, TrendUp } from "@phosphor-icons/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
  Line,
} from "recharts";

const Stat = ({ label, value, sub, testid }) => (
  <div className="border border-gray-200 p-6" data-testid={testid}>
    <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 font-medium">{label}</div>
    <div className="font-mono-num text-2xl font-semibold mt-3">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-2">{sub}</div>}
  </div>
);

const monthLabel = (mk) => {
  const [y, m] = mk.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Ags", "Sep", "Okt", "Nov", "Des"];
  return `${names[parseInt(m) - 1]} ${y.slice(2)}`;
};

const formatShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e9) return `${(v / 1e9).toFixed(1)}M`;
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(0)}jt`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(0)}rb`;
  return String(v);
};

const firstDayOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const today = () => new Date().toISOString().slice(0, 10);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [margin, setMargin] = useState([]);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportType, setReportType] = useState("payments");
  const [start, setStart] = useState(firstDayOfMonth());
  const [end, setEnd] = useState(today());

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data));
    api.get("/dashboard/monthly-sales").then((r) => setMonthly(r.data.data || []));
    api.get("/dashboard/monthly-margin").then((r) => setMargin(r.data.data || []));
  }, []);

  const chartData = useMemo(() =>
    monthly.map((m) => ({
      month: monthLabel(m.month),
      Cash: m.cash,
      Kredit: m.credit,
    })), [monthly]);

  const marginData = useMemo(() =>
    margin.map((m) => ({
      month: monthLabel(m.month),
      Pembelian: m.purchase_cost,
      Penjualan: m.sales_revenue,
      Margin: m.margin,
    })), [margin]);

  const downloadReport = async () => {
    try {
      const endpoint = reportType === "piutang"
        ? `/reports/piutang.xlsx?as_of=${end}`
        : `/reports/payments.xlsx?start=${start}&end=${end}`;
      const r = await api.get(endpoint, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = reportType === "piutang" ? `piutang-${end}.xlsx` : `pembayaran-${start}-${end}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Laporan diunduh");
      setReportOpen(false);
    } catch (e) {
      toast.error("Gagal mengunduh laporan");
    }
  };

  if (!data) return <div className="p-10 text-sm text-gray-500">Memuat...</div>;

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Ringkasan penjualan, piutang, dan keterlambatan cicilan"
        actions={
          <div className="flex gap-2">
            <Dialog open={reportOpen} onOpenChange={setReportOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="report-btn"
                  className="rounded-none bg-white border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white uppercase tracking-wider text-xs h-10"
                >
                  <DownloadSimple size={14} className="mr-2" /> Laporan Excel
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none max-w-[95vw] max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-display text-xl">Download Laporan</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Jenis Laporan</Label>
                    <Select value={reportType} onValueChange={setReportType}>
                      <SelectTrigger data-testid="report-type" className="mt-2 rounded-none">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="payments">Pembayaran Cicilan</SelectItem>
                        <SelectItem value="piutang">Piutang (Angsuran Belum Lunas)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {reportType === "payments" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs uppercase tracking-wider">Dari Tanggal</Label>
                        <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-2 rounded-none" data-testid="report-start" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider">Sampai Tanggal</Label>
                        <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-2 rounded-none" data-testid="report-end" />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Per Tanggal</Label>
                      <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-2 rounded-none" data-testid="report-asof" />
                      <div className="text-xs text-gray-500 mt-2">Piutang & keterlambatan dihitung per tanggal ini</div>
                    </div>
                  )}
                  <Button onClick={downloadReport} data-testid="report-download" className="w-full rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-11">
                    <DownloadSimple size={14} className="mr-2" /> Download Excel
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Link to="/sales">
              <Button data-testid="header-new-sale" className="rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-10">
                Transaksi Baru <ArrowRight className="ml-2" size={14} />
              </Button>
            </Link>
          </div>
        }
      />
      <div className="p-4 md:p-6 lg:p-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-gray-200 -m-px">
          <Stat testid="stat-cash" label="Penjualan Cash" value={formatIDR(data.total_cash)} sub={`${data.count_cash} transaksi`} />
          <Stat testid="stat-credit" label="Penjualan Kredit" value={formatIDR(data.total_credit)} sub={`${data.count_credit} transaksi`} />
          <Stat testid="stat-outstanding" label="Sisa Piutang" value={formatIDR(data.total_outstanding)} sub="belum tertagih" />
          <Stat testid="stat-collected" label="Terkumpul" value={formatIDR(data.total_collected)} sub="dari cicilan" />
        </div>

        {/* Monthly sales chart */}
        <div className="mt-10 border border-gray-200 p-6" data-testid="monthly-chart-card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-gray-500 font-medium">
                <ChartBar size={12} weight="bold" /> Tren Penjualan
              </div>
              <h2 className="font-display text-2xl font-semibold mt-1">Cash vs Kredit — 12 Bulan Terakhir</h2>
            </div>
          </div>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="month" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#6B7280" fontSize={11} tickFormatter={formatShort} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ border: "1px solid #E5E7EB", borderRadius: 0, fontSize: 12 }} formatter={(v) => formatIDR(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" />
                <Bar dataKey="Cash" fill="#0A0A0A" radius={0} />
                <Bar dataKey="Kredit" fill="#F97316" radius={0} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Margin chart */}
        <div className="mt-10 border border-gray-200 p-6" data-testid="margin-chart-card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-gray-500 font-medium">
                <TrendUp size={12} weight="bold" /> Margin Bulanan
              </div>
              <h2 className="font-display text-2xl font-semibold mt-1">Pembelian vs Penjualan — 12 Bulan</h2>
            </div>
          </div>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <ComposedChart data={marginData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="month" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#6B7280" fontSize={11} tickFormatter={formatShort} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ border: "1px solid #E5E7EB", borderRadius: 0, fontSize: 12 }} formatter={(v) => formatIDR(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" />
                <Bar dataKey="Pembelian" fill="#94A3B8" radius={0} />
                <Bar dataKey="Penjualan" fill="#0A0A0A" radius={0} />
                <Line type="monotone" dataKey="Margin" stroke="#F97316" strokeWidth={2.5} dot={{ r: 4, fill: "#F97316" }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-gray-500 font-medium">Status Piutang</div>
              <h2 className="font-display text-2xl font-semibold mt-1">Keterlambatan per Kategori</h2>
            </div>
            <Link to="/overdue" className="text-xs uppercase tracking-wider text-gray-600 hover:text-black flex items-center gap-1">
              Lihat detail <ArrowRight size={12} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-gray-200 -m-px">
            {Object.entries(data.by_category).map(([name, val]) => (
              <div key={name} className="border border-gray-200 p-6" data-testid={`cat-${name.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2" style={{ backgroundColor: val.color }} />
                  <div className="text-[10px] tracking-[0.2em] uppercase text-gray-600 font-medium">{name}</div>
                </div>
                <div className="font-mono-num text-2xl font-semibold mt-3">{val.count}</div>
                <div className="text-xs text-gray-500 mt-1 font-mono-num">{formatIDR(val.amount)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-0 border border-gray-200 -m-px">
          <div className="border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-2">
              <Warning size={18} weight="bold" className="text-[#E63946]" />
              <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 font-medium">Total Menunggak</div>
            </div>
            <div className="font-mono-num text-3xl font-semibold" data-testid="overdue-total">{data.overdue_count}</div>
            <div className="text-xs text-gray-500 mt-1">angsuran lewat jatuh tempo</div>
          </div>
          <div className="border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={18} weight="bold" className="text-[#F59E0B]" />
              <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 font-medium">Jatuh Tempo 7 Hari</div>
            </div>
            <div className="font-mono-num text-3xl font-semibold" data-testid="upcoming-total">{data.upcoming_count}</div>
            <div className="text-xs text-gray-500 mt-1">angsuran akan jatuh tempo</div>
          </div>
        </div>
      </div>
    </div>
  );
}
